import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownList,
  type DropdownListGroup,
} from "@/components/ui/dropdown-list/dropdown-list";
import type { IDropdownListItem } from "@/components/ui/dropdown-list/dropdown-list-item";
import { TextAreaField } from "@/components/common/text-field";
import {
  insolvencySchema,
  type InsolvencyFormData,
  type InsolvencyAPIPayload,
} from "@/schemas/users/insolvency";
import {
  useCreateInsolvency,
  useUpdateInsolvency,
  useGetApplicantProperties,
} from "@/queries/users/insolvencies.api";
import type { InsolvencyType } from "@/types/entities/insolvencies";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon, useToast } from "@elaniintc/goes-design-system";
import * as DialogPrimitive from "@radix-ui/react-dialog";

const PAYMENT_DEADLINE_UNITS = [
  { value: "HOURS", label: "Horas" },
  { value: "DAYS", label: "Días" },
  { value: "WEEKS", label: "Semanas" },
  { value: "MONTHS", label: "Meses" },
];

import {
  Button,
  GroupTextField,
  Input,
  Switch,
  TextField,
} from "@elaniintc/goes-design-system";
import {
  Controller,
  FormProvider,
  useForm,
  type SubmitHandler,
} from "react-hook-form";

interface InsolvencyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userId: string;
  mode?: "add" | "edit";
  insolvencyData?: InsolvencyType;
}

const InsolvencyFormModal = ({
  isOpen,
  onClose,
  userName,
  userId,
  mode = "add",
  insolvencyData,
}: InsolvencyFormModalProps) => {
  const createInsolvency = useCreateInsolvency();
  const updateInsolvency = useUpdateInsolvency(insolvencyData?.id || "");
  const { showToast } = useToast();

  const isEditMode = mode === "edit";

  const shouldFetchProperties =
    !isEditMode ||
    (isEditMode && insolvencyData?.insolvencyable_type === "property");

  const { data: propertiesData, isLoading: isLoadingProperties } =
    useGetApplicantProperties(shouldFetchProperties ? userId : "");

  const methods = useForm<InsolvencyFormData>({
    resolver: zodResolver(insolvencySchema),
    defaultValues:
      isEditMode && insolvencyData
        ? {
            reason: insolvencyData.reason || "",
            reference: insolvencyData.reference || "",
            code: insolvencyData.code || "",
            can_appeal: insolvencyData.can_appeal,
            payment_deadline_value: insolvencyData.payment_deadline_value,
            payment_deadline_unit: insolvencyData.payment_deadline_unit,
            amount: parseFloat(insolvencyData.amount) ?? undefined,
            insolvencyable_id: insolvencyData.insolvencyable_id || userId,
            insolvencyable_type:
              insolvencyData.insolvencyable_type || "applicant",
            status: insolvencyData.status || "ACTIVE",
            comments: "",
          }
        : {
            reason: "",
            reference: "",
            code: "",
            can_appeal: false,
            payment_deadline_value: 0,
            payment_deadline_unit: "DAYS",
            amount: undefined,
            insolvencyable_id: userId,
            insolvencyable_type: "applicant",
            status: "ACTIVE",
            comments: "",
          },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    register,
  } = methods;

  const canAppeal = watch("can_appeal");

  const dropdownOptions: DropdownListGroup[] = [
    {
      groupId: "applicant",
      groupLabels: ["Tramitador"],
      items: [
        {
          value: userId,
          label: userName,
        },
      ],
    },
    ...((propertiesData?.data?.length ?? 0) > 0
      ? [
          {
            groupId: "properties",
            groupLabels: ["Inmuebles"],
            items:
              propertiesData?.data?.map((property) => ({
                value: property.id,
                label: property.name,
              })) || [],
          },
        ]
      : []),
  ];

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<InsolvencyFormData> = async (data) => {
    if (isEditMode) {
      // For edit mode, only send comments
      const updatePayload = {
        comments: data.comments || "",
        reason: data.reason || "",
        status: data.status || "ACTIVE",
      };

      try {
        await updateInsolvency.mutateAsync(
          updatePayload as InsolvencyAPIPayload,
        );
        showToast({
          title: "Infracción actualizada",
          description: "La infracción ha sido actualizada exitosamente.",
          options: {
            type: "success",
            duration: 4000,
          },
        });
        handleClose();
      } catch (error) {
        console.error("Error updating insolvency:", error);
        showToast({
          title: "Error",
          description:
            "No se pudo actualizar la infracción. Por favor, intenta nuevamente.",
          options: {
            type: "error",
            duration: 4000,
          },
        });
      }
    } else {
      // For add mode, send all fields
      const apiPayload: InsolvencyAPIPayload = {
        ...(data.reason &&
          data.reason.trim() !== "" && { reason: data.reason }),
        ...(data.reference &&
          data.reference.trim() !== "" && { reference: data.reference }),
        ...(data.code && data.code.trim() !== "" && { code: data.code }),
        ...(data.comments &&
          data.comments.trim() !== "" && { comments: data.comments }),
        can_appeal: data.can_appeal,
        payment_deadline_value: data.payment_deadline_value,
        payment_deadline_unit: data.payment_deadline_unit,
        amount: data.amount,
        insolvencyable_id: data.insolvencyable_id,
        insolvencyable_type: data.insolvencyable_type,
        status: data.status || "ACTIVE",
      };

      try {
        await createInsolvency.mutateAsync(apiPayload);
        showToast({
          title: "Infracción creada",
          description: "La infracción ha sido creada exitosamente.",
          options: {
            type: "success",
            duration: 4000,
          },
        });
        handleClose();
      } catch (error) {
        console.error("Error creating insolvency:", error);
        showToast({
          title: "Error",
          description:
            "No se pudo crear la infracción. Por favor, intenta nuevamente.",
          options: {
            type: "error",
            duration: 4000,
          },
        });
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        type="modal"
        className="max-w-[600px] p-0 gap-0"
        showCloseButton={false}
      >
        <DialogHeader className="py-4 px-8 flex-row w-full justify-between items-center border-b border-neutral-silent">
          <DialogTitle className="text-h4 font-semibold text-heading">
            {isEditMode ? "Editar infracción" : "Añadir infracción"}
          </DialogTitle>
          <DialogPrimitive.Close asChild>
            <Button
              type="button"
              size="icon"
              variant="quiet"
              aria-label="Cerrar"
            >
              <Icon name="close" type="fas" />
            </Button>
          </DialogPrimitive.Close>
        </DialogHeader>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-3 px-8"
          >
            <div className="flex flex-col gap-4 overflow-y-auto max-h-[calc(90vh-240px)]">
              <TextField
                label="Motivo"
                required
                error={errors.reason?.message}
                placeholder="Título de la infracción"
                disabled={isEditMode}
                {...register("reason")}
              />

              <Controller
                name="amount"
                control={control}
                render={({ field: { onChange, value, ...rest } }) => {
                  return (
                    <TextField
                      label="Monto"
                      required
                      prefix="$"
                      error={errors.amount?.message}
                      placeholder="$0.00"
                      type="number"
                      inputMode="decimal"
                      value={value}
                      onChange={(e) => {
                        const numValue = Number(e.target.value);
                        onChange(numValue);
                      }}
                      disabled={isEditMode}
                      {...rest}
                    />
                  );
                }}
              />

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="can-appeal"
                  className="text-body-loud font-semibold"
                >
                  Posibilidad de apelar infracción
                  <span className="text-danger">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <Controller
                    name="can_appeal"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        id="can-appeal"
                        checked={value}
                        onCheckedChange={onChange}
                        disabled={isEditMode}
                      />
                    )}
                  />
                  <div className="flex items-start gap-2">
                    <p className="text-sm text-body-quiet">
                      {canAppeal ? "Sí" : "No es posible apelar la infracción"}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-body-quiet leading-xl">
                  Si está activo, el tramitador podrá apelar para anular su
                  infracción.
                </p>
              </div>

              <Controller
                name="insolvencyable_id"
                control={control}
                render={({ field }) => {
                  const selectedLabel =
                    dropdownOptions
                      .flatMap((group) => group.items)
                      .find((item) => item.value === field.value)?.label || "";

                  return (
                    <div className="flex flex-col gap-2">
                      <label className="text-body-loud font-semibold">
                        Asociar a<span className="text-danger">*</span>
                      </label>
                      {errors.insolvencyable_id && (
                        <p className="text-sm text-danger">
                          {errors.insolvencyable_id.message}
                        </p>
                      )}
                      <DropdownList
                        placeholder={
                          isLoadingProperties
                            ? "Cargando opciones..."
                            : "Selecciona una persona o inmueble..."
                        }
                        widthClassName="w-full"
                        heightClassName={
                          dropdownOptions.length > 1 ? "h-35" : "h-25"
                        }
                        disabled={isEditMode || isLoadingProperties}
                        options={dropdownOptions}
                        selectedValue={
                          field.value
                            ? {
                                value: field.value,
                                label: selectedLabel,
                              }
                            : undefined
                        }
                        toggleSelection={(item: IDropdownListItem) => {
                          field.onChange(item.value);
                          setValue(
                            "insolvencyable_type",
                            item.value === userId ? "applicant" : "property",
                          );
                        }}
                      />
                    </div>
                  );
                }}
              />

              <GroupTextField
                label="Plazo para realizar el pago"
                required
                error={errors.payment_deadline_value?.message}
              >
                <Controller
                  name="payment_deadline_value"
                  control={control}
                  render={({ field: { onChange, value, ...rest } }) => {
                    const displayValue = value > 0 ? value.toString() : "";
                    return (
                      <Input
                        placeholder="Escribe un número"
                        type="text"
                        group="left"
                        inputMode="numeric"
                        value={displayValue}
                        onChange={(e) => {
                          const numValue = parseInt(e.target.value, 10);
                          onChange(numValue);
                        }}
                        disabled={isEditMode}
                        className="w-full py-[10.5px] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        {...rest}
                      />
                    );
                  }}
                />
                <Controller
                  name="payment_deadline_unit"
                  control={control}
                  render={({ field }) => (
                    <DropdownList
                      options={PAYMENT_DEADLINE_UNITS}
                      selectedValue={PAYMENT_DEADLINE_UNITS.find(
                        (unit) => unit.value === field.value,
                      )}
                      toggleSelection={(item) => {
                        field.onChange(item.value);
                      }}
                      disabled={isEditMode}
                      widthClassName="w-32"
                      heightClassName="h-43"
                      group="right"
                    />
                  )}
                />
              </GroupTextField>
              <p className="text-xs text-body-quiet -mt-4 leading-xl">
                Si no quieres poner un limite de tiempo para el pago deja este
                campo vacío. Si deseas que el pago sea insolvente de manera
                inmediata escribe el numero 0.
              </p>

              <TextAreaField
                label="Comentario"
                required
                error={errors.comments?.message}
                placeholder="Escribe un comentario..."
                rows={4}
                {...register("comments")}
              />
            </div>

            <DialogFooter className="pb-3 px-3">
              <div className="flex justify-end gap-3 w-full">
                <Button
                  type="button"
                  variant="tertiary"
                  size="default"
                  onClick={handleClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  size="default"
                  disabled={
                    !isValid ||
                    (isEditMode
                      ? updateInsolvency.isPending
                      : createInsolvency.isPending)
                  }
                  isLoading={
                    isEditMode
                      ? updateInsolvency.isPending
                      : createInsolvency.isPending
                  }
                >
                  {isEditMode
                    ? updateInsolvency.isPending
                      ? "Guardando..."
                      : "Guardar cambios"
                    : createInsolvency.isPending
                      ? "Guardando..."
                      : "Añadir"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default InsolvencyFormModal;
